import type { ReactNode } from 'react';
import { usePortal } from '../modal/usePortal';

export type PortalProps = {
  /**
   * Content to render inside the portal.
   */
  children: ReactNode;
  /**
   * Optional ID for the portal container element.
   * Multiple portals with the same ID will share the same container.
   * @default 'paction-ui-portal'
   */
  containerId?: string;
};

/**
 * Portal component that renders children into a DOM node outside
 * the parent component's DOM hierarchy.
 *
 * Useful for:
 * - Modals and dialogs
 * - Dropdowns and popovers
 * - Tooltips
 * - Any UI that needs to escape parent stacking contexts
 *
 * @example
 * ```tsx
 * <Portal>
 *   <div className={styles.modal}>Modal content</div>
 * </Portal>
 * ```
 */
export function Portal({ children, containerId }: PortalProps) {
  const { render } = usePortal(containerId);
  return render(children);
}
